import { beginMessage } from '../message';
import { MessageTag } from '../messageTags';
import { BlackjackActions, Role } from '../enums';
import type { BinaryReader } from '../BinaryReader';
import type {
  GetGamePayload,
  BlackjackPreviewPayload,
  RoundResultPayload,
  EndGamePayload,
  DisconnectPayload,
} from '../models/blackjackGame';

/**
 * Blackjack in-game codec. Mirrors BlackjackGame.cs (server) and BlackjackUI.cs (Unity reader).
 * The server decides ALL game logic; the client only sends actions and renders the state it
 * receives. Field order below is the wire order — do not reorder.
 */

/** Hand string "AS-7D-TS" → ["AS","7D","TS"]; empty string → [] (an empty hand). */
export function parseHand(s: string): string[] {
  return s ? s.split('-') : [];
}

// ---- C2S (encode) ----

/**
 * Hit(66): optionally carries an int echo of my last-rendered hand value (a desync guard the server
 * checks via TryGetInt). Omit it to deal unconditionally. Either way the new card arrives in the
 * follow-up GetGame broadcast, never as a reply to this message.
 */
export function encodeHit(handValue?: number): Uint8Array {
  const w = beginMessage(MessageTag.Hit);
  if (handValue !== undefined) w.putInt(handValue);
  return w.toUint8Array();
}

/** Stand(67): empty body — end my turn. */
export function encodeStand(): Uint8Array {
  return beginMessage(MessageTag.Stand).toUint8Array();
}

/**
 * Bet(69): a single `int` amount (NOT a long). The server validates it is in
 * [currentBet, min(bothBalances)] and, on success, auto-deals a card — so never pair this with Hit.
 */
export function encodeBet(amount: number): Uint8Array {
  return beginMessage(MessageTag.Bet).putInt(amount).toUint8Array();
}

/** Refresh(52): empty body — ask the server to re-send GetGame (resync). */
export function encodeRefresh(): Uint8Array {
  return beginMessage(MessageTag.Refresh).toUint8Array();
}

/**
 * GetGame(28) as a C2S REQUEST: a single string gameUniqueId. Used ONLY on reconnect resume to ask the
 * server to re-send the live table state (the server replies with the normal GetGame broadcast). During
 * regular play the client never requests GetGame — it only receives the server's broadcasts. Mirrors the
 * Unity reconnect branch (Network.cs) and BlackjackGame.cs GetGame handler.
 */
export function encodeGetGame(gameUniqueId: string): Uint8Array {
  return beginMessage(MessageTag.GetGame).putString(gameUniqueId).toUint8Array();
}

// ---- S2C (decode) ----

/** GetGame(28): full per-player table state — 16 ordered fields. */
export function decodeGetGame(r: BinaryReader): GetGamePayload {
  return {
    myHand: parseHand(r.getString()),
    oppHand: parseHand(r.getString()),
    minBet: r.getInt(),
    bet: r.getInt(),
    player1Balance: r.getLong(),
    player2Balance: r.getLong(),
    dealerRole: r.getShort() as Role,
    playerRole: r.getShort() as Role,
    actions: r.getUShortArray() as BlackjackActions[],
    turn: r.getShort() as Role,
    turnNextTrigger: r.getInt(),
    player1TimeBank: r.getInt(),
    player2TimeBank: r.getInt(),
    player1TotalWin: r.getShort(),
    player2TotalWin: r.getShort(),
    myHandValue: r.getInt(),
  };
}

/** GamePreview(65): spectator state — GetGame without legal actions or a hand value. */
export function decodeBlackjackPreview(r: BinaryReader): BlackjackPreviewPayload {
  return {
    dealerHand: parseHand(r.getString()),
    playerHand: parseHand(r.getString()),
    minBet: r.getInt(),
    bet: r.getInt(),
    player1Balance: r.getLong(),
    player2Balance: r.getLong(),
    dealerRole: r.getShort() as Role,
    playerRole: r.getShort() as Role,
    turn: r.getShort() as Role,
    turnNextTrigger: r.getInt(),
    player1TimeBank: r.getInt(),
    player2TimeBank: r.getInt(),
    player1TotalWin: r.getShort(),
    player2TotalWin: r.getShort(),
  };
}

/** ShowRoundResult(71): both hands revealed, values, winner, and the Persian reason. */
export function decodeShowRoundResult(r: BinaryReader): RoundResultPayload {
  return {
    dealerHand: parseHand(r.getString()),
    playerHand: parseHand(r.getString()),
    dealerValue: r.getInt(),
    playerValue: r.getInt(),
    winner: r.getShort() as Role,
    reason: r.getString(),
  };
}

/** ShowEndGameResult(74): the terminal match summary (two Persian strings). */
export function decodeShowEndGameResult(r: BinaryReader): EndGamePayload {
  return { resultText: r.getString(), reasonText: r.getString() };
}

/** Disconnect(20): opponent leaving/returning + their remaining time bank. */
export function decodeDisconnect(r: BinaryReader): DisconnectPayload {
  return { isLeaving: r.getBool(), timeBank: r.getInt() };
}
